import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import {
	MenuFoldOutlined,
	MenuUnfoldOutlined,
} from '@ant-design/icons';
import {
	UserPlusIcon,
	UserMinusIcon,
	ClockIcon,
	CheckCircleIcon,
	UserCircleIcon,
	IdentificationIcon,
	XCircleIcon,
	QueueListIcon,
	DocumentCheckIcon,
	DocumentMinusIcon,
	PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { Avatar, Button, Layout, Menu, Drawer, Grid, Spin } from 'antd';
import { Outlet, useNavigate } from 'react-router-dom';

import Context from '../../context/Context';
import ImageIcon from '../../assets/image.png';
import { avatarBgColor } from '../../utils/utils';
import useIdleLogout from '../../hooks/useIdleLogout';
//import { LogoutOutlined } from "@ant-design/icons";
import { FiLogOut } from "react-icons/fi";

import styles from './privateLayout.module.scss';

const { Header, Sider, Content, Footer } = Layout;
const { useBreakpoint } = Grid;
const menuIcon = (Icon) => (
	<Icon style={{ width: 20, height: 20 }} />
);
const PrivateLayout = () => {
	const { isAuthenticated, loading } = useContext(Context);
	const userDetails = JSON.parse(localStorage.getItem('user-onboard-data'));
	const navigate = useNavigate();
	const [openLogout, setOpenLogout] = useState(false);

	const [collapsed, setCollapsed] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const screens = useBreakpoint();
	const isMobile = !screens.md;
	useEffect(() => { if (!isMobile) setMobileMenuOpen(false); }, [isMobile]);
	const collapseTimeoutRef = useRef(null);

// Collapse sider after X seconds when mouse leaves
const startAutoCollapseTimer = () => {
	if (isMobile) return;
	if (collapseTimeoutRef.current) {
		clearTimeout(collapseTimeoutRef.current);
	}
	collapseTimeoutRef.current = setTimeout(() => {
		setCollapsed(true);
	}, 1000); // 3 seconds – adjust as needed
};

const cancelAutoCollapseTimer = () => {
	if (collapseTimeoutRef.current) {
		clearTimeout(collapseTimeoutRef.current);
	}
};

	// Check if the user is an admin
	const isAdmin = userDetails?.isAdmin === 'Y';
	const restrictedNh = ['HNF', 'PANCARE'].includes(
	userDetails?.nhName?.trim()?.toUpperCase()
	);
	const filteredItems = useMemo(() => {
	const items = [
		// NON-ADMIN
		{
			key: 'non-admin-new-user',
			label: 'New User',
			icon: menuIcon(UserPlusIcon),
			children: [
				{ key: '/new-user/create', label: 'Create User', icon: menuIcon(UserPlusIcon) },
				{ key: '/new-user/pending', label: 'Pending List', icon: menuIcon(ClockIcon) },
				{ key: '/new-user/completed', label: 'Completed List', icon: menuIcon(CheckCircleIcon) },
			],
		},
		{
			key: 'non-admin-deactivate',
			label: 'Deactivate',
			icon: menuIcon(UserMinusIcon),
			children: [
				{ key: '/deactivate-user/deactivate', label: 'Deactivate User', icon: menuIcon(UserMinusIcon) },
				{ key: '/deactivate-user/pending', label: 'Pending Deactivation', icon: menuIcon(ClockIcon) },
				{ key: '/deactivate-user/completed', label: 'Completed Deactivation', icon: menuIcon(XCircleIcon) },
			],
		},
		{
			key: 'non-admin-modify-user',
			label: 'Amendment',
			icon: menuIcon(IdentificationIcon),
			children: [
				{ key: '/ModifyUserNRIC/ModifyNRIC', label: 'Modify User NRIC', icon: menuIcon(PencilSquareIcon) },
				{ key: '/ModifyUserNRIC/pending', label: 'Pending User NRIC Changes', icon: menuIcon(ClockIcon) },
				{ key: '/ModifyUserNRIC/completed', label: 'Completed User NRIC Changes', icon: menuIcon(CheckCircleIcon) },
			],
		},
		// ADMIN
		{
			key: 'admin-dashboard',
			label: 'Dashboard',
			icon: menuIcon(QueueListIcon),
			children: [
				{ key: '/admin/dashboard', label: 'User Dashboard', icon: menuIcon(QueueListIcon) },
			],
		},
		{
			key: 'admin-new-user',
			label: 'New User',
			icon: menuIcon(UserPlusIcon),
			children: [
				{ key: '/admin/new-user/create', label: 'Create User', icon: menuIcon(UserPlusIcon) },
				{ key: '/admin/new-user/pending', label: 'Pending List', icon: menuIcon(ClockIcon) },
				{ key: '/admin/new-user/completed', label: 'Completed List', icon: menuIcon(CheckCircleIcon) },
			],
		},
		{
			key: 'admin-deactivate',
			label: 'Deactivate',
			icon: menuIcon(UserMinusIcon),
			children: [
				{ key: '/admin/deactivate-user/deactivate', label: 'Deactivate User', icon: menuIcon(UserMinusIcon) },
				{ key: '/admin/deactivate-user/pending', label: 'Pending Deactivation', icon: menuIcon(ClockIcon) },
				{ key: '/admin/deactivate-user/completed', label: 'Completed Deactivation', icon: menuIcon(XCircleIcon) },
			],
		},

		{
			key: 'admin-modify-user-details',
			label: 'Amendment',
			icon: menuIcon(IdentificationIcon),
			children: [
				{   key: '/admin/ModifyUserNRIC/ModifyNRIC',label: 'Modify User NRIC',icon: menuIcon(PencilSquareIcon),},
				{   key: '/admin/ModifyUserNRIC/pending',label: 'Pending User NRIC Changes',icon: menuIcon(ClockIcon), },
				{   key: '/admin/ModifyUserNRIC/completed',label: 'Completed User NRIC Changes',icon: menuIcon(CheckCircleIcon),},
			],
		},
		{
			key: 'admin-batch-status',
			label: 'Batch Status',
			icon: menuIcon(QueueListIcon),
			children: [
				{ key: '/admin/batch-status/creation', label: 'Creation Status', icon: menuIcon(DocumentCheckIcon) },
				{ key: '/admin/batch-status/deactivation', label: 'Deactivation Status', icon: menuIcon(DocumentMinusIcon) },
			],
		},
		{
			key: 'admin-modify-user-information',
			label: 'Modify User Info',
			icon: menuIcon(PencilSquareIcon),
			children: [
				{
					key: '/admin/modify-user-information',
					label: 'Modify NRIC',
					icon: menuIcon(PencilSquareIcon),
				},
				{
					key: '/admin/ad-password-reset',
					label: 'AD Password Reset',
					icon: menuIcon(PencilSquareIcon),
				},
			],
		},
	];

	return isAdmin
		? items.filter((item) => item.key.startsWith('admin'))
		: items.filter((item) => {
				if (
					restrictedNh &&
					item.key === 'non-admin-modify-user'
				) {
					return false;
				}

				return item.key.startsWith('non-admin');
			});

	}, [isAdmin, restrictedNh]);


	const handleLogout = () => {
        import('../../auth/auth-service').then(({ authService }) => authService.logout());
    };

	useIdleLogout(handleLogout);

	if (loading) {
		return (
			<div className="loading-wrapper">
				<Spin size="large" />
			</div>
		);
	}

	const onClickMenu = ({ key }) => {
		setMobileMenuOpen(false);
		navigate(key);
	};

	return (
		<div className={styles['layout-wrapper']}>
			{isAuthenticated ? (
				<Layout>
					{!isMobile && <Sider
					className={styles.siderFullNavy}
					trigger={null}
					collapsible
					collapsed={collapsed}
					width={260}
					onMouseEnter={cancelAutoCollapseTimer}
					onMouseLeave={startAutoCollapseTimer}
					>
						<Button
							type="text"
							icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
							onClick={() => {
								setCollapsed(!collapsed);

								// If expanded, auto-collapse after delay
								if (collapsed === true) {
									cancelAutoCollapseTimer();
								} else {
									startAutoCollapseTimer();
								}
							}}
							className={styles.burgerBtn}
						/>
						<Menu
							mode="inline"
							items={filteredItems}
							className={styles.menuCustom}
							theme="dark"
							onClick={onClickMenu}
							defaultSelectedKeys={[window.location.pathname]}
							/>

					</Sider>}
					<Drawer title="Navigation" placement="left" width={280} open={isMobile && mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} className={styles.mobileDrawer} styles={{ body: { padding: 0, background: '#061b32' } }} destroyOnClose>
						<Menu mode="inline" items={filteredItems} theme="dark" onClick={onClickMenu} selectedKeys={[window.location.pathname]} className={styles.menuCustom} />
					</Drawer>
					<Layout>
						<Header className={styles.headerBar}>
							<div className={styles.headerLeft}>{isMobile && <Button type="text" aria-label="Open navigation" icon={<MenuUnfoldOutlined />} onClick={() => setMobileMenuOpen(true)} className={styles.mobileMenuButton} />}</div>

							<div className={styles.headerCenter}>
								<div className={styles.titleCard}>User Access Management</div>
							</div>

							<div
								className={styles.logoutWrapper}
								onMouseLeave={() => setOpenLogout(false)}
							>
								<div
									className={styles.avatarBtn}
									onClick={() => setOpenLogout(!openLogout)}
								>
									<Avatar
										className={styles.avatarInitial}
										style={{
											backgroundColor:
												avatarBgColor[userDetails?.userId?.charAt(0)] ||
												avatarBgColor.default,
										}}
									>
										{userDetails?.userId?.charAt(0)?.toUpperCase()}
									</Avatar>
								</div>

								{openLogout && (
									<div className={styles.logoutPanel} onClick={handleLogout}>
										<FiLogOut size={14} />
										<span>Log Out</span>
									</div>
								)}
							</div>

						</Header>

						<Content className={styles['content']}>
							<Outlet />
						</Content>
						<Footer style={{ textAlign: 'center', padding: '24px 18px' }}>
							<div className={styles.footer}>
								<div className={styles.left}>
								<div className={styles.logoBlock}>
									<div className={styles.logoCard}>
									<img
										src={ImageIcon}
										alt="Elder & Community Caresuite"
									/>
									</div>

									<p className={styles.copyright}>
									©2023 All rights reserved.
									</p>
								</div>
								</div>

								<div className={styles.right}>
								Release Version 2.0.0
								</div>
							</div>
						</Footer>

					</Layout>
				</Layout>
			) : null}
		</div>
	);
};

export default PrivateLayout;
